// uploads

import * as bootstrap from 'bootstrap';
import { DateTime } from 'luxon';
import axios from 'smg/axios';
import * as clipboard from 'smg/clipboard';
import * as models from 'smg/customer-api/models';
import { Uploads } from 'smg/customer-api/v2/uploads';
import { DownloadURL, Source } from 'smg/download';
import * as formatting from 'smg/formatting';
import * as mime from 'smg/mime';
import Modal from 'smg/modal';
import { Picture } from 'smg/picture';
import * as template from 'smg/template';
import * as toast from 'smg/toast';
import * as uploadSMG from 'smg/uploads';
import { PageLoader } from '../account';
import { AccountReady } from '../account-session';

declare interface myWindow extends Window {
    readonly cloudFront: string;
}
declare const window: myWindow;

void main();

async function main() {
    if (await AccountReady) {
        return;
    }

    const addNewTemplate = document.querySelector('#addNewTemplate') as HTMLElement;
    const cardTemplate = document.querySelector('#cards') as HTMLTemplateElement;
    const parent = document.querySelector('#uploads-container') as HTMLElement;

    // Upload a file
    const addNew = document.querySelectorAll('.addNew');

    if (addNew.length) {
        addNew.forEach(el => {
            el.addEventListener('click', () => {
                const body = (document.querySelector('#modal-upload-template') as HTMLTemplateElement).content.cloneNode(true) as DocumentFragment;
                const uploadsLabel = body.querySelector('#modal-uploads') as HTMLLabelElement;
                const spinner = uploadsLabel.querySelector('.spinner-border') as HTMLElement;

                const m: Modal = new Modal({
                    outsideClosable: false,
                    title: 'Upload files to your account',
                    body,
                    onSubmit: async () => {
                        const uploadIDs = await u.getUploadIDs();
                        if (uploadIDs.length === 0) {
                            throw new Error('Please provide a file');
                        } else {
                            return axios.patch('/v2/uploads/keep', {
                                'uploadIDs': uploadIDs,
                            }).then(() => {
                                toast.Success(`File${uploadIDs.length === 1 ? '' : 's'} have been uploaded!`);
                                pl.load();
                            });
                        }
                    },
                });

                const u = new uploadSMG.Uploader(uploadsLabel, {
                    confirmBtn: m.$element.querySelector('[type="submit"]') as HTMLButtonElement,
                    thumbnails: document.querySelector('#modal-uploads-thumbnails') as HTMLElement,
                    thumbnailHeight: '5rem',
                    thumbnailMaxHeight: 'auto',
                    start: () => {
                        spinner.classList.remove('d-none');
                    },
                    stop: () => spinner.classList.add('d-none'),
                    onchange: () => {
                        const errorEl = document.querySelector('.modal-error') as HTMLElement;
                        errorEl.classList.add('d-none');
                    },
                });
            });
        });
    }

    const pl: PageLoader<models.Upload> = new PageLoader<models.Upload>({
        afterLoad: () => {
            parent.append(addNewTemplate);
            addNewTemplate.classList.remove('d-none');
        },
        parent: document.querySelector('#uploads-container') as HTMLElement,
        get: () => Uploads({
            sort: pl.sort(),
            max: pl.currentPageSize(),
            p: pl.currentPage(),
            q: pl.search() || undefined,
            data: 'id,name,mime,key,created,size',
        }).then(resp => {
            const rows = resp.results;
            return { rows, nextPage: resp.nextPage };
        }),
        draw: upload => {
            const card = template.Clone(cardTemplate).firstElementChild as HTMLElement;

            // Name
            const nameEl = card.querySelectorAll('.upload-name');
            const name = upload.name;

            if (nameEl) {
                nameEl.forEach(el => {
                    el.textContent = name;
                });
            }

            // Url
            const urlEl = card.querySelector('.upload-link') as HTMLElement;
            const a = document.createElement('a') as HTMLAnchorElement;
            const url = upload.url(window.cloudFront);

            if (urlEl) {
                a.textContent = url;
                a.href = url;
                a.setAttribute('target', '_blank');
                if (upload.mime && mime.WebSafeMime(upload.mime)) {
                    a.setAttribute('data-fancybox', String(upload.id));
                }
                urlEl.append(a);
            }

            // File Size
            const sizeEl = card.querySelectorAll('.file-size');

            if (sizeEl) {
                sizeEl.forEach(el => {
                    el.textContent = formatting.HumanFileSize(upload.size);
                });
            }

            // Date created
            const dateEl = card.querySelectorAll('.date-created');

            if (dateEl) {
                dateEl.forEach(el => {
                    el.textContent = upload.created.isValid ? upload.created.toLocaleString(DateTime.DATE_SHORT) : '';
                });
            }

            // Download
            const downloadEl = card.querySelector('.download-button') as HTMLAnchorElement;

            if (downloadEl) {
                const s: Source = {
                    url: url,
                };
                downloadEl.addEventListener('click', async () => {
                    await DownloadURL(name, s);
                });
            }

            // Copy link
            const copyLink = card.querySelector('.copy-link') as HTMLButtonElement;

            if (copyLink) {
                copyLink.addEventListener('click', () => {
                    clipboard.Copy(url);
                    const t = new bootstrap.Tooltip(copyLink, {
                        title: 'Copied!',
                        trigger: 'manual',
                    });
                    t.show();
                    setTimeout(() => t.dispose(), 2000);
                });
            }

            // Thumbnail
            const thumbContainer = card.querySelector('.page-thumbnail') as HTMLElement;

            if (thumbContainer) {
                const a = document.createElement('a');
                const img = document.createElement('img') as HTMLImageElement;
                a.append(img); // Append to parent first so Picture can wrap it

                img.style.maxHeight = '5rem';
                img.style.maxWidth = '5rem';
                void Picture(img, url, {
                    displayWidth: 80,
                    alt: name,
                    mime: upload.mime,
                    fancyboxThumb: true,
                });

                a.setAttribute('data-fancybox', 'uploads');
                a.href = url;

                thumbContainer.append(a);
            }

            return card;
        },
    });

    parent?.previousElementSibling?.classList.add('d-none');

    pl.load();
}
